// src/components/ProtecaoDisparo.js — a faixa de proteção de todo disparo em massa.
//
// Out/26 (blindagem contra banimento). Um cliente em teste disparou oferta
// de crédito para 211 pessoas que nunca tinham falado com ele, e a Meta
// baniu número e conta. Esta faixa aparece nas três telas de envio em
// massa — Contatos (BulkTemplateDialog), o lote do chat e os Disparos
// agendados — com o que o servidor disse no preflight
// (`/templates/preflight`):
//
//   - tema proibido no template  → bloqueia (o servidor recusa com 422);
//   - conta com problema na Meta → bloqueia (423);
//   - período de teste           → mostra quanto ainda cabe hoje;
//   - contatos que nunca conversaram com a empresa, e a caixa de opt-in,
//     obrigatória (sem ela o servidor recusa com 428).
//
// Quem barra de verdade é o servidor (services/protecao_disparo); aqui é
// só dizer antes, com as mesmas frases.
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

// Motivo pelo qual o disparo não pode sair, ou null. A tela usa pra
// desligar o botão e trocar o texto dele.
export function bloqueioDoDisparo(preflight) {
  if (!preflight) return null;
  if (preflight.tema_proibido) return 'tema';
  if (preflight.conta_meta) return 'conta';
  if (preflight.teste && preflight.teste.restante_hoje <= 0) return 'teste';
  return null;
}

// Cores pelos tokens do tema (`--ch-*`, globais desde o ChatPage.css), com o
// valor antigo de reserva: no escuro do chat e do CRM, o âmbar e o verde
// fixos do tema claro ficavam apagados (out/26, CRM moderno).
export default function ProtecaoDisparo({ preflight, total, optin, onOptin, cor = 'var(--ch-text, #0f172a)', corSuave = 'var(--ch-text2, #64748b)' }) {
  if (!preflight) return null;
  const frios = preflight.frios || 0;
  const quantos = total ?? (preflight.contacts || []).length;
  const bloqueio = bloqueioDoDisparo(preflight);
  const alerta = { borderRadius: 2, fontSize: '0.8rem', bgcolor: 'transparent' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {preflight.tema_proibido && (
        <Alert severity="error" variant="outlined" sx={alerta}>{preflight.tema_proibido}</Alert>
      )}
      {preflight.conta_meta && (
        <Alert severity="error" variant="outlined" sx={alerta}>
          {preflight.conta_meta} O atendimento a quem escreve continua funcionando.
        </Alert>
      )}
      {preflight.teste && (
        <Alert severity={preflight.teste.restante_hoje > 0 ? 'warning' : 'error'} variant="outlined" sx={alerta}>
          No período de teste, o envio em massa vai até {preflight.teste.teto} templates por dia.
          {preflight.teste.restante_hoje > 0
            ? ` Hoje ainda cabem ${preflight.teste.restante_hoje}${quantos > preflight.teste.restante_hoje ? ': o disparo para quando chegar no limite.' : '.'}`
            : ' O limite de hoje já foi atingido. O envio um a um pelo chat continua liberado.'}
        </Alert>
      )}
      {!bloqueio && (
        <Box sx={{ border: `1px solid ${frios ? 'var(--ch-amber, #f59e0b)' : 'var(--ch-bdr2, rgba(100,116,139,.35))'}`, borderRadius: '12px', p: '10px 12px' }}>
          {frios > 0 && (
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--ch-amber-d, #b45309)', mb: 0.5 }}>
              {frios} de {quantos} {frios === 1 ? 'contato nunca conversou' : 'contatos nunca conversaram'} com a sua empresa.
            </Typography>
          )}
          {frios > 0 && (
            <Typography sx={{ fontSize: '0.76rem', color: corSuave, mb: 0.75, lineHeight: 1.5 }}>
              Mensagem para quem não pediu é o que mais gera denúncia, e denúncia é o que faz a Meta
              banir o número e a conta.
            </Typography>
          )}
          <FormControlLabel
            control={(
              <Checkbox
                size="small" checked={!!optin} onChange={(e) => onOptin?.(e.target.checked)}
                inputProps={{ 'aria-label': 'Confirmo que estes contatos autorizaram receber mensagens' }}
                sx={{ color: corSuave, '&.Mui-checked': { color: 'var(--ch-green, #16a34a)' } }}
              />
            )}
            label={(
              <Typography sx={{ fontSize: '0.8rem', color: cor, lineHeight: 1.4 }}>
                Confirmo que estes contatos autorizaram receber mensagens da minha empresa no WhatsApp.
              </Typography>
            )}
            sx={{ alignItems: 'flex-start', m: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
          />
        </Box>
      )}
    </Box>
  );
}
